'use client';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { useDashboardStore } from '@/store/dashboard.store';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { ClipboardList, ChevronLeft, ChevronRight, X, SlidersHorizontal, Plus } from 'lucide-react';
import NewRequestForm from '@/components/requests/NewRequestForm';
import RequestCatalogCard from '@/components/marketplace/RequestCatalogCard';
import { notify } from '@/lib/toast';
import { getDisplayName } from '@/lib/displayName';



interface Props {
  initialData: any;
  categories: any[];
}

export default function MarketplaceClient({ initialData, categories }: Props) {
  const { isAuthenticated } = useAuthStore();
  const { isSpecialist, specialistId } = useDashboardStore();
  const router = useRouter();
  const qc = useQueryClient();

  const [page, setPage] = useState(1);
  const [categoryId, setCategoryId] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [urgency, setUrgency] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('');
  const [showInProgress, setShowInProgress] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [budgetMin, setBudgetMin] = useState<string>('');
  const [budgetMax, setBudgetMax] = useState<string>('');
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [price, setPrice] = useState('');
  const [comment, setComment] = useState('');

  const isFiltered = page > 1 || !!categoryId || !!city || !!urgency || !!sortBy || showInProgress || !!budgetMin || !!budgetMax;

  const { data, isLoading } = useQuery({
    queryKey: ['marketplace', page, categoryId, city, urgency, sortBy, showInProgress, budgetMin, budgetMax],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (categoryId) params.set('category', categoryId);
      if (city) params.set('city', city);
      if (urgency) params.set('urgency', urgency);
      if (sortBy) params.set('sortBy', sortBy);
      if (showInProgress) params.set('showInProgress', 'true');
      if (budgetMin) params.set('budgetMin', budgetMin);
      if (budgetMax) params.set('budgetMax', budgetMax);
      return api.get(`/requests/public?${params}`).then(r => r.data.data);
    },
    initialData: isFiltered ? undefined : initialData,
    staleTime: 30_000,
  });

  const respondMutation = useMutation({
    mutationFn: ({ requestId }: { requestId: string }) =>
      api.post(`/requests/${requestId}/respond/${specialistId}`, {
        proposed_price: Number(price),
        comment,
      }),
    onSuccess: () => {
      notify.success('Отклик отправлен');
      setRespondingId(null);
      setPrice('');
      setComment('');
      qc.invalidateQueries({ queryKey: ['marketplace'] });
    },
    onError: (err: any) => notify.apiError(err, 'Ошибка отклика'),
  });

  const handleRespond = (requestId: string) => {
    if (!isAuthenticated()) { router.push('/login'); return; }
    if (!isSpecialist) { router.push('/become-specialist'); return; }
    setRespondingId(requestId);
  };

  const resetFilters = () => {
    setCategoryId(''); setCity(''); setUrgency(''); setSortBy(''); setShowInProgress(false); setBudgetMin(''); setBudgetMax(''); setPage(1);
  };

  const hasActiveFilters = !!categoryId || !!city || !!urgency || !!sortBy || showInProgress || !!budgetMin || !!budgetMax;

  const requests: any[] = data?.requests ?? [];
  const totalPages: number = data?.pages ?? 1;
  const total: number = data?.total ?? 0;

  return (
    <>
    <div className="min-h-screen bg-bg-subtle">
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Шапка */}
        <div className="flex items-center gap-3 mb-6">
          <Link href="/" className="p-2 rounded-lg hover:bg-gray-200 transition-colors text-gray-500">
            <ChevronLeft size={20} />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-text-primary">Биржа заказов</h1>
            <p className="text-sm text-gray-500">
              {total > 0 ? `${total} запросов` : 'Открытые запросы от заказчиков'}
            </p>
          </div>
          {isAuthenticated() && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-brand text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-brand-dark transition-colors"
            >
              <Plus size={15} />
              Создать заказ
            </button>
          )}
          <button
            onClick={() => setShowFilters(v => !v)}
            style={showFilters || hasActiveFilters ? { backgroundColor: 'var(--color-brand)', color: '#fff' } : {}}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
              showFilters || hasActiveFilters ? '' : 'border-border bg-bg-surface text-text-secondary hover:bg-bg-subtle'
            }`}
          >
            <SlidersHorizontal size={15} />
            Фильтры
            {hasActiveFilters && (
              <span className="w-5 h-5 rounded-full bg-bg-surface/30 text-xs flex items-center justify-center font-bold">
                {[categoryId, city, urgency, sortBy, showInProgress].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Панель фильтров */}
        {showFilters && (
          <div className="bg-bg-surface rounded-xl shadow-sm border border-border p-4 mb-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

              {/* Категория */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block font-medium">Категория</label>
                <select
                  value={categoryId}
                  onChange={e => { setCategoryId(e.target.value); setPage(1); }}
                  className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30"
                >
                  <option value="">Все категории</option>
                  {categories.filter((s: any) => s.children?.length > 0).map((section: any) => (
                    <optgroup key={section.id} label={section.name}>
                      {section.children?.map((cat: any) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Срочность */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block font-medium">Срочность</label>
                <select
                  value={urgency}
                  onChange={e => { setUrgency(e.target.value); setPage(1); }}
                  className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30"
                >
                  <option value="">Любая</option>
                  <option value="today">Сегодня</option>
                  <option value="this_week">На этой неделе</option>
                  <option value="this_month">В этом месяце</option>
                  <option value="flexible">Гибкий срок</option>
                </select>
              </div>

              {/* Город */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block font-medium">Город</label>
                <input
                  type="text"
                  value={city}
                  onChange={e => { setCity(e.target.value); setPage(1); }}
                  placeholder="Например: Иркутск"
                  className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30"
                />
              </div>

              {/* Сортировка */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block font-medium">Сортировка</label>
                <select
                  value={sortBy}
                  onChange={e => { setSortBy(e.target.value); setPage(1); }}
                  className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30"
                >
                  <option value="">Новые сначала</option>
                  <option value="budget_desc">Бюджет: высокий → низкий</option>
                  <option value="budget_asc">Бюджет: низкий → высокий</option>
                </select>
              </div>
            </div>

            {/* Бюджет */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block font-medium">Бюджет от (₽)</label>
                <input
                  type="number"
                  value={budgetMin}
                  onChange={e => { setBudgetMin(e.target.value); setPage(1); }}
                  placeholder="0"
                  min="0"
                  className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block font-medium">Бюджет до (₽)</label>
                <input
                  type="number"
                  value={budgetMax}
                  onChange={e => { setBudgetMax(e.target.value); setPage(1); }}
                  placeholder="Любой"
                  min="0"
                  className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30"
                />
              </div>
            </div>

            {/* Статус "В работе" */}
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={() => { setShowInProgress(v => !v); setPage(1); }}
                className={`relative w-10 h-5 rounded-full transition-colors ${showInProgress ? 'bg-brand' : 'bg-gray-200'}`}
                style={showInProgress ? { backgroundColor: 'var(--color-brand)' } : {}}
              >
                <span className={`absolute top-[2px] left-[2px] w-4 h-4 bg-white rounded-full shadow transition-transform ${showInProgress ? 'translate-x-[20px]' : 'translate-x-0'}`} />
              </button>
              <span className="text-sm text-text-ui">Показывать заказы «В работе»</span>
            </div>

            {/* Сброс */}
            {hasActiveFilters && (
              <div className="flex justify-end pt-1 border-t border-border-subtle">
                <button
                  onClick={resetFilters}
                  className="text-sm text-gray-400 hover:text-text-secondary flex items-center gap-1"
                >
                  <X size={14} /> Сбросить все фильтры
                </button>
              </div>
            )}
          </div>
        )}

        {/* Список */}
        {isLoading ? (
          <div className="space-y-3">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="bg-bg-surface rounded-xl border border-border p-5 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
                <div className="h-3 bg-bg-muted rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <ClipboardList size={40} className="mx-auto mb-3 opacity-30" />
            <p>Нет запросов по выбранным фильтрам</p>
            {hasActiveFilters && (
              <button onClick={resetFilters} className="mt-3 text-sm text-brand hover:underline">
                Сбросить фильтры
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((req: any) => (
              <RequestCatalogCard
                key={req.id}
                req={req}
                isSpecialist={isSpecialist}
                specialistId={specialistId}
                onRespond={handleRespond}
                respondingId={respondingId}
                onCancelRespond={() => { setRespondingId(null); setPrice(''); setComment(''); }}
                price={price}
                comment={comment}
                onPriceChange={setPrice}
                onCommentChange={setComment}
                onSubmitRespond={(id) => respondMutation.mutate({ requestId: id })}
                respondIsPending={respondMutation.isPending}
              />
            ))}
          </div>
        )}

        {/* Пагинация */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg border border-border bg-bg-surface hover:bg-bg-subtle disabled:opacity-40 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm text-text-secondary px-3">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-lg border border-border bg-bg-surface hover:bg-bg-subtle disabled:opacity-40 transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>

    {showCreateModal && (
      <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 px-4 py-8 overflow-y-auto">
        <div className="w-full max-w-lg mt-8">
          <NewRequestForm
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => { setShowCreateModal(false); qc.invalidateQueries({ queryKey: ['marketplace'] }); }}
          />
        </div>
      </div>
    )}
    </>
  );
}
